import { Box, Stack, ThemeProvider } from '@mui/material';
import { gsap } from 'gsap';
import * as React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { generateTheme } from '../themes';
import { RPMText } from './subComponents/RPMText';
import { RPMButton } from './subComponents/RPMButton';


gsap.registerPlugin(ScrollTrigger);

const CAMPAIGN_DEFAULTS_ENDPOINT = 'https://b.cardcam.io/api/paddle-discount-defaults';
let campaignDefaultsPromise = null;

const loadCampaignDefaults = () => {
  if (campaignDefaultsPromise) return campaignDefaultsPromise;
  campaignDefaultsPromise = fetch(CAMPAIGN_DEFAULTS_ENDPOINT)
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .catch(() => null);
  return campaignDefaultsPromise;
};

const substituteTokens = (text, data) => {
  if (!text) return '';
  return text.replace(/\[([a-zA-Z_][a-zA-Z0-9_]*)\]/g, (_, key) => {
    if (data && Object.prototype.hasOwnProperty.call(data, key) && data[key] != null) {
      return String(data[key]);
    }
    return '';
  });
};

// Toggle: 'geometric' = current rectangles, 'chroma' = soft grainy gradient waves
const HERO_STYLE = 'chroma';

// Generate 100 shapes programmatically with varied parallax + scrub delay for trail effect
// px = parallax speed (negative=up, positive=down for counter-movement ~20% go opposite)
// sd = scrub delay in seconds (higher = more lag = trail effect)
const SHAPES = (() => {
  // Seeded pseudo-random for deterministic output
  let seed = 42;
  const rand = () => { seed = (seed * 16807 + 0) % 2147483647; return seed / 2147483647; };

  const widths = [110, 115, 120, 125, 130, 135, 140];
  const heights = [18, 20, 22, 24, 26, 28, 30, 32, 35];
  const lefts = [-22, -20, -18, -15, -12, -10, -8, -5];
  const rotations = [-22, -20, -18, -16, -14, -12, -10, -8, -6, -4, -3];
  const angles = [110, 115, 120, 125, 130, 135, 140, 145, 150, 155, 160, 165, 170, 175];

  return Array.from({ length: 100 }, (_, i) => {
    const pick = (arr) => arr[Math.floor(rand() * arr.length)];
    const goesOpposite = rand() < 0.2; // ~20% drift in opposite direction
    const speed = goesOpposite
      ? 0.04 + rand() * 0.06   // positive (down): 0.04–0.10
      : -(0.06 + rand() * 0.20); // negative (up): -0.06 to -0.26

    return {
      w: `${pick(widths)}%`,
      h: `${pick(heights)}%`,
      t: `${-24 + i * 1.22}%`,  // spread evenly from -24% to ~98%
      l: `${pick(lefts)}%`,
      r: pick(rotations),
      o: 0.02 + rand() * 0.02,  // opacity 0.03–0.08
      ga: pick(angles),
      px: Math.round(speed * 100) / 100,
      sd: 0.2 + rand() * 1.6,   // scrub delay 0.2–1.8s
    };
  });
})();

const HeroShapes = () => {
  const shapesRef = useRef([]);
  // Track opacity state per shape
  const opacityState = useRef(SHAPES.map(() => 1));

  useEffect(() => {
    const els = shapesRef.current.filter(Boolean);
    if (els.length === 0) return;

    // Start some shapes hidden
    els.forEach((el, i) => {
      if (i % 3 === 0) {
        el.style.opacity = 0;
        opacityState.current[i] = 0;
      }
    });

    let timeout;
    const animate = () => {
      const idx = Math.floor(Math.random() * els.length);
      const el = els[idx];
      const isVisible = opacityState.current[idx] === 1;

      if (isVisible) {
        gsap.to(el, {
          opacity: 0,
          duration: 3,
          ease: 'power2.inOut',
        });
        opacityState.current[idx] = 0;
      } else {
        gsap.to(el, {
          opacity: 1,
          duration: 3,
          ease: 'power2.inOut',
        });
        opacityState.current[idx] = 1;
      }

      timeout = setTimeout(animate, 1000 + Math.random() * 2500);
    };

    timeout = setTimeout(animate, 800);
    return () => {
      clearTimeout(timeout);
      els.forEach(el => gsap.killTweensOf(el));
    };
  }, []);

  return (
    <Box sx={{
      position: 'absolute',
      inset: 0,
      overflow: 'hidden',
      pointerEvents: 'none',
      zIndex: 0,
    }}>
      {SHAPES.map((s, i) => (
        <Box
          key={i}
          ref={el => shapesRef.current[i] = el}
          style={{
            opacity: 1,
            background: `linear-gradient(${s.ga}deg, rgba(255,255,255,${s.o * 1.2}) 0%, rgba(255,255,255,${s.o * 0.3}) 100%)`,
          }}
          sx={{
            position: 'absolute',
            width: s.w,
            height: s.h,
            top: s.t,
            left: s.l,
            mixBlendMode: 'overlay',
            transform: `rotate(${s.r}deg)`,
            borderRadius: '2rem',
          }}
        />
      ))}
    </Box>
  );
};

// Band config
const BAND_ROTATION_FACTOR = 0; // max rotation in degrees (±half)

const BANDS = (() => {
  const count = 6;
  const maxHeight = 440; // tallest band in px
  let seed = 99;
  const rand = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  const bands = [];
  // Colors: teal → blue → purple (tallest to shortest)
  const colors = [
    '#5d00ff', // tallest
    '#5539cb',
    '#4c45c0',
    '#3b58b5',
    '#2a6fa8',
    '#1a839a', // shortest
  ];

  for (let i = 0; i < count; i++) {
    const t = (i + 1) / count;
    const height = Math.round(maxHeight * Math.sqrt(t));
    const r = (rand() - 0.5) * BAND_ROTATION_FACTOR;
    bands.push({ height: `${height}px`, o: 0.15, r, color: colors[i] });
  }
  return bands;
})();

const HeroShapesChroma = () => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    if (!window.matchMedia('(max-width: 899px)').matches) return;
    const bars = containerRef.current.querySelectorAll('.hero-band');
    if (!bars.length) return;

    const tweens = Array.from(bars).map((bar, i) =>
      gsap.fromTo(
        bar,
        { yPercent: 0 },
        {
          yPercent: -(i + 1) * 22,
          ease: 'none',
          scrollTrigger: {
            trigger: '#topSlice',
            start: 'top top',
            end: 'bottom top',
            scrub: 0.5,
            invalidateOnRefresh: true
          }
        }
      )
    );

    return () => {
      tweens.forEach((t) => {
        if (t.scrollTrigger) t.scrollTrigger.kill();
        t.kill();
      });
    };
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: { xs: 'fixed', md: 'absolute' },
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0,
        mixBlendMode: 'screen',
      }}
    >
      {BANDS.map((b, i) => (
        <Box
          key={i}
          className="hero-band"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: b.height,
            background: b.color,
            opacity: b.o,
            transform: `rotate(${b.r}deg)`,
            transformOrigin: 'bottom center',
            mixBlendMode: 'screen',
            willChange: 'transform',
          }}
        />
      ))}
    </Box>
  );
};

const TopHeroSlice = ({ data, color = "#fff", direction = "row" }) => {
  const {
    title,
    description,
    imageUrl,
    videoUrl,
    imagePosition,
    colorTheme,
    cta,
    button_sub_title,
    topBanner,
    buttonStyle = 'filled'
  } = data;

  const ccRef = useRef(null);
  const topimgContainerRef = useRef(null);
  const containerRef = useRef(null);

  const theme = generateTheme(colorTheme || 'white');

  const hasManualBannerText = Boolean(topBanner && (topBanner.text || topBanner.text2));
  const hasCampaignBannerText = Boolean(topBanner && (topBanner.campaignText || topBanner.campaignText2));

  const [campaignDefaults, setCampaignDefaults] = useState(null);

  useEffect(() => {
    if (!topBanner || hasManualBannerText || !hasCampaignBannerText) return;
    let cancelled = false;
    loadCampaignDefaults().then(data => {
      if (!cancelled) setCampaignDefaults(data);
    });
    return () => { cancelled = true; };
  }, [topBanner, hasManualBannerText, hasCampaignBannerText]);

  const effectiveBanner = (() => {
    if (!topBanner) return null;
    if (hasManualBannerText) {
      return { text: topBanner.text, text2: topBanner.text2 };
    }
    if (!hasCampaignBannerText) return null;
    if (!campaignDefaults || campaignDefaults.show_need_a_discount_on_signup !== true) return null;
    const text = substituteTokens(topBanner.campaignText || '', campaignDefaults);
    const text2 = substituteTokens(topBanner.campaignText2 || '', campaignDefaults);
    if (!text && !text2) return null;
    return { text, text2 };
  })();

  const bannerVisible = Boolean(effectiveBanner);

  // Create data structure that RPMText and RPMButton expect
  const sliceData = {
    title1: [{ text: title }],
    subtitle: [{ text: description }],
    linktext: [{ text: cta?.text || '' }]
  };

  console.log("sliceData:", sliceData);

  const buttonLink = cta?.url ? { url: cta.url } : null;
  const secondaryCta = button_sub_title?.[0]?.text || data.secondary_cta_text;

  const Tagline = () => {
    // Get tagline data from the WordPress block data
    const taglineText = data.tagline;
    const taglineImage = data.tagline_image;
    
    if (!taglineText) return null;
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, px: { xs: "0", sm: "0" } }}>
        {taglineImage && (
          <img 
            src={taglineImage.url || taglineImage} 
            alt={taglineImage.alt || ''} 
            style={{ width: '70px', height: '70px' }}
          />
        )}
        <p style={{
          color: '#222',
          WebkitTextFillColor: '#222',
          fontSize: '1.125rem',
          lineHeight: 1.7,
          fontWeight: 400,
          margin: 0,
        }}>
          {taglineText}
        </p>
      </Box>
    );
  };

  useEffect(() => {
    const tl = gsap.timeline({
      paused: true,
      defaults: {
        duration: 2.5,
        ease: 'power2.out',
        delay: 1,
      },
    });

    tl.to('.rainbow-text', {
      backgroundPosition: '0 100%'
    });

    tl.play();

    const tr = ScrollTrigger.create({
      trigger: '#transparentTop',
      start: 'bottom 80%',
      end: 'top 20%',
      onEnterBack: () => {
        if (!tl.isActive()) {
          tl.restart();
        }
      },
      markers: false,
      invalidateOnRefresh: true,
    });

    // Mobile-only: as the hero scrolls up into dark background, fade the
    // headline text fill from the default dark gradient to teal so it stays
    // readable against the darker portion of the hero gradient.
    const mqlMobile = window.matchMedia('(max-width: 899px)');
    let mobileFade = null;
    const setupMobileFade = () => {
      if (!mqlMobile.matches) return;
      const targets = document.querySelectorAll('.rainbow-text, .rainbow-text h1');
      if (!targets.length) return;
      mobileFade = gsap.timeline({
        scrollTrigger: {
          trigger: '#topSlice',
          start: 'top top',
          end: '+=50%',
          scrub: 0.3,
          invalidateOnRefresh: true,
          markers: false
        }
      });
      mobileFade.fromTo(
        targets,
        { WebkitTextFillColor: 'transparent' },
        { WebkitTextFillColor: '#0fc9e2', ease: 'none', duration: 1 }
      );
      mobileFade.to(
        targets,
        { WebkitTextFillColor: '#ffffff', ease: 'none', duration: 1 }
      );
    };
    setupMobileFade();

    return () => {
      tl.kill();
      tr.kill();
      if (mobileFade) {
        if (mobileFade.scrollTrigger) mobileFade.scrollTrigger.kill();
        mobileFade.kill();
      }
    };
  }, []);

  // Render banner outside hero stacking context
  const renderBanner = () => {
    if (!effectiveBanner) return null;
    const sizeMap = {
      Small:  { py: "0.4rem", fontSize: { xs: "0.75rem", md: "0.85rem" } },
      Medium: { py: "0.7rem", fontSize: { xs: "0.85rem", md: "1rem" } },
      Large:  { py: "1rem",   fontSize: { xs: "1rem",    md: "1.15rem" } }
    };
    const size = sizeMap[topBanner.size] || sizeMap.Small;
    const bannerSx = {
      position: "fixed",
      top: "64px",
      left: 0,
      width: "100%",
      backgroundColor: topBanner.bgColor,
      color: topBanner.textColor,
      textAlign: "center",
      py: size.py,
      px: "1rem",
      fontSize: size.fontSize,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      zIndex: 10999,
      textDecoration: "none",
      display: "block",
      borderRadius: "0 !important",
      transition: "filter 0.2s ease",
      cursor: topBanner.link ? "pointer" : "default",
      "&:hover": topBanner.link ? { filter: "brightness(1.15)", textDecoration: "underline" } : {},
    };
    const arrow = topBanner.link ? ' →' : '';
    const bannerContent = (
      <>
        <Box component="span" sx={{ display: "inline" }}>
          {effectiveBanner.text}
        </Box>
        {effectiveBanner.text2 && (
          <Box component="span" sx={{ display: { xs: "block", md: "inline" }, ml: { md: "1rem" } }}>
            {effectiveBanner.text2}{arrow}
          </Box>
        )}
        {!effectiveBanner.text2 && arrow}
      </>
    );
    return topBanner.link ? (
      <Box component="a" href={topBanner.link} sx={bannerSx}>
        {bannerContent}
      </Box>
    ) : (
      <Box sx={bannerSx}>{bannerContent}</Box>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      {renderBanner() && createPortal(renderBanner(), document.body)}
      <Stack id="topSlice" direction="column"
        spacing={0} sx={{
          overflowX: "clip",
          width: "100%",
          position: { sm: "relative", md: "fixed" },
          top: 0,
          minHeight: "100%",
          zIndex: 0,
          background: { xs: "linear-gradient(180deg, #F5F1EB 20vh, #0091A5 55vh, #301B71 75vh)", md: "linear-gradient(180deg, #F5F1EB 30%, #0091A5 75%, #301B71 100%)" },
          pt: { xs: bannerVisible ? "96px" : "0px", md: bannerVisible ? "120px" : "64px" },
          pb: 0,
        }}
        alignItems={{ xs: 'center', sm: 'center', md: 'center' }}
        justifyContent="center"
      >
        {/* Decorative overlay shapes — toggle via HERO_STYLE */}
        {HERO_STYLE === 'chroma' ? <HeroShapesChroma /> : <HeroShapes />}

        <Stack id="topMainContent"
          px={6}
          direction={{ sm: "column", md: "row" }}
          spacing={{ xs: 0, sm: 0, md: 0 }}
          sx={{
            width: "100%",
            position: { xs: "sticky", md: "relative" },
            top: { xs: bannerVisible ? "160px" : "64px", md: "auto" },
            zIndex: 0,
            alignItems: { xs: "center", sm: "center", md: "center" },
            mt: { xs: 0, sm: 0, lg: "0" },
            mb: { xs: "0", sm: "0", md: "35vh", lg: "15vh" },
            mx: { xs: "1rem", sm: "1rem", md: "2vw" }
          }}
        >
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            spacing={0}
            ref={containerRef}
            sx={{ width: { xs: "100%", md: "50%" } }}
          >
            <Box
              style={{ position: "relative" }}
              sx={{ mt: { sm: "2rem", md: "0rem" }, px: { xs: "0.5rem", sm: "2rem" }, maxWidth: "600px", width: "100%" }}
            >
              <Box
                id="topText"
                className='rainbow-text'
                sx={{ overflow: 'visible', paddingTop: "1rem" }}
              >
                <RPMText
                  content={sliceData.title1}
                  variant="h1Bold"
                  elementRenderType={"h1"}
                  sx={{
                    p: "0px",
                    color: color,
                    mb: "0px",
                    textAlign: "left",
                    alignContent: "flex-start",
                  }}
                />
              </Box>
              <p
                style={{
                  color: "#222",
                  WebkitTextFillColor: "#222",
                  paddingLeft: "1rem",
                  marginTop: "1.5rem",
                  letterSpacing: "0.00938em",
                  lineHeight: "1.7",
                  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                  fontWeight: 400,
                  fontSize: "1.125rem",
                  textAlign: "left",
                }}
              >
                {description}
              </p>

              <Tagline />

              {buttonLink && (
                <Stack direction="column" spacing={1} sx={{ pt: "1.5rem", alignItems: { xs: "center", md: "flex-start" } }}>
                  <RPMButton
                    link={buttonLink}
                    linktext={sliceData?.linktext[0].text}
                    type={buttonStyle === 'outlined' ? 'outlined' : 'contained'}
                    size="large"
                    sx={{ maxWidth: { xs: "100%", sm: "320px" } }}
                  />
                  {secondaryCta && (
                    <RPMButton
                      link={{ url: data.secondary_cta_url || buttonLink.url }}
                      linktext={secondaryCta}
                      type="text"
                      size="large"
                      sx={{ maxWidth: { xs: "100%", sm: "320px" }, color: '#fff', width: '100%', textAlign: 'center' }}
                    />
                  )}
                </Stack>
              )}
            </Box>
          </Stack>

        </Stack>

        {(imageUrl || videoUrl) && (
          <Box id="topImage"
            ref={topimgContainerRef}
            sx={{
              position: { xs: "relative", md: "absolute" },
              right: 0,
              top: 0,
              bottom: 0,
              width: { xs: "100%", md: "50%" },
              zIndex: { xs: 2, md: 1 },
              overflow: "hidden",
              mt: { xs: "4vh", sm: "4vh", md: 0 },
              pointerEvents: "none",
            }}
          >
            {videoUrl ? (
              <video
                id="topiPhone"
                ref={ccRef}
                autoPlay
                muted
                loop
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                  pointerEvents: "none",
                }}
              >
                <source src={videoUrl} type="video/mp4" />
              </video>
            ) : (
              <img
                id="topiPhone"
                alt="Cardcam - Digitala visitkort på iPhone med Apple Wallet"
                ref={ccRef}
                src={imageUrl}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                  pointerEvents: "none",
                }}
                loading='eager'
                fetchpriority='high'
              />
            )}
          </Box>
        )}

        {/* Wallet badges moved to TextImageSlice secondary image */}
      </Stack>

      <Box
        id="transparentTop"
        sx={{
          backgroundColor: "#ff00000",
          height: { md: "100%", md: "100vh" },
          zIndex: -200,
          position: "relative"
        }}
      />
    </ThemeProvider>
  );
};

export default TopHeroSlice